import cx from "classnames";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { TicketSeverity, TicketType } from "@gamedev-web/one-gaming-crm";
import { ButtonType, HeroButton } from "@gaming/xds-react-js";

import { SelectDropdown } from "../../components/SelectDropdown";
import { LoadingSpinner } from "../../components/Spinner/Spinner";
import { PageType } from "../../constants";
import {
  AttachmentInput,
  SubmitTicketRequest,
  TicketSeverityLabel,
  TicketSubCategory,
} from "../../models/ticketing";
import { RouteNames } from "../../partnerConfig/routeNames";
import { OnboardingUrls } from "../../partnerConfig/routes";
import { ticketingActions } from "../../redux/actions/ticketingActions";
import {
  getTicketingCategories,
  getTicketingIsOptionsFetched,
  getTicketingIsSubmitting,
  getTicketingSubCategories,
  getTicketingSubmitError,
  getTicketingSubmitSuccess,
  getTicketingTeams,
  getTicketingTranslationMap,
} from "../../redux/selectors/ticketingSelectors";
import { AccessDeniedPage } from "../AccessDeniedPage/AccessDeniedPage";
import * as formStyles from "./SubmitTicketPage.module.css";
import { useTicketingStrings } from "./ticketingStrings";
import * as pageStyles from "./TicketingTicketDetailPage.module.css";
import { translateValue, TranslationEntity, TranslationField } from "./translateValue";
import { useRequireTicketingAccess } from "./useRequireTicketingAccess";

type SubmitPageParams = {
  sellerId: string;
};

const severityOptions = [
  { value: TicketSeverity.low, label: TicketSeverityLabel.Low },
  { value: TicketSeverity.medium, label: TicketSeverityLabel.Medium },
  { value: TicketSeverity.high, label: TicketSeverityLabel.High },
  { value: TicketSeverity.critical, label: TicketSeverityLabel.Critical },
];

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    [{ size: ["small", false, "large", "huge"] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "code-block"],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "list",
  "indent",
  "align",
  "blockquote",
  "code-block",
  "script",
  "link",
];

let linkSanitizeConfigured = false;

const focusableSelector =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const moveFocusOutside = (container: HTMLElement, reverse: boolean) => {
  const scope = container.closest<HTMLElement>("main") ?? document.documentElement;
  const allFocusable = Array.from(scope.querySelectorAll<HTMLElement>(focusableSelector));
  const firstIdx = allFocusable.findIndex(el => container.contains(el));
  let lastIdx = firstIdx;
  for (let i = allFocusable.length - 1; i >= firstIdx; i--) {
    if (container.contains(allFocusable[i])) {
      lastIdx = i;
      break;
    }
  }

  if (reverse) {
    const target =
      firstIdx > 0 ? allFocusable[firstIdx - 1] : allFocusable[allFocusable.length - 1];
    target?.focus();
  } else {
    const target = lastIdx < allFocusable.length - 1 ? allFocusable[lastIdx + 1] : allFocusable[0];
    target?.focus();
  }
};

export const TicketingSubmitPage: React.FC = () => {
  const { isSignedIn, isVerifyChecked, isVerified } = useRequireTicketingAccess();
  const strings = useTicketingStrings();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { sellerId } = useParams<SubmitPageParams>();
  const categories = useSelector(getTicketingCategories);
  const subCategories = useSelector(getTicketingSubCategories);
  const isOptionsFetched = useSelector(getTicketingIsOptionsFetched);
  const isSubmitting = useSelector(getTicketingIsSubmitting);
  const submitSuccess = useSelector(getTicketingSubmitSuccess);
  const submitError = useSelector(getTicketingSubmitError);
  const translationMap = useSelector(getTicketingTranslationMap);
  const teams = useSelector(getTicketingTeams);
  const submitTeams = useMemo(() => teams.filter(t => !t.isFrozen), [teams]);

  const [ReactQuill, setReactQuill] = useState<React.ComponentType<Record<string, unknown>> | null>(
    null
  );

  useEffect(() => {
    Promise.all([import("react-quill-new"), import("react-quill-new/dist/quill.snow.css")]).then(
      ([mod]) => {
        if (!linkSanitizeConfigured) {
          const Quill = mod.Quill;
          const Link = Quill.import("formats/link") as { sanitize: (url: string) => string };
          const origSanitize = Link.sanitize.bind(Link);
          Link.sanitize = (url: string): string => {
            if (url && !/^https?:\/\//i.test(url) && !/^mailto:/i.test(url)) {
              return origSanitize(`https://${url}`);
            }
            return origSanitize(url);
          };
          linkSanitizeConfigured = true;
        }
        setReactQuill(() => mod.default as unknown as React.ComponentType<Record<string, unknown>>);
      }
    );
  }, []);

  const descriptionEditorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const wrapper = descriptionEditorRef.current;
    if (!wrapper) {
      return;
    }
    const handleTab = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        const editor = wrapper.querySelector(".ql-editor");
        if (editor?.contains(document.activeElement)) {
          e.preventDefault();
          e.stopPropagation();
          moveFocusOutside(wrapper, e.shiftKey);
        }
      }
    };
    wrapper.addEventListener("keydown", handleTab, true);
    return () => wrapper.removeEventListener("keydown", handleTab, true);
  }, []);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionTextLength, setDescriptionTextLength] = useState(0);
  const [severity, setSeverity] = useState("");
  const [teamId, setTeamId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [subCategoryId, setSubCategoryId] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");
  const [attachments, setAttachments] = useState<AttachmentInput[]>([
    { url: "https://", description: "" },
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(ticketingActions.clearSubmitStatus());
  }, [dispatch]);

  useEffect(() => {
    if (!isOptionsFetched) {
      dispatch(ticketingActions.fetchOptions.start(sellerId));
    }
  }, [isOptionsFetched, dispatch, sellerId]);

  // Pre-select category/subcategory from URL params (e.g. ?cat=123&subcat=456)
  useEffect(() => {
    if (!isOptionsFetched || categories.length === 0) {
      return;
    }
    const params = new URLSearchParams(location.search);
    const catParam = params.get("cat");
    const subcatParam = params.get("subcat");

    if (catParam && categories.some(c => c.id === catParam)) {
      setCategoryId(catParam);
      if (
        subcatParam &&
        subCategories.some(sc => sc.id === subcatParam && sc.categoryId === catParam)
      ) {
        setSubCategoryId(subcatParam);
      }
    }
    // Only run once when options load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOptionsFetched]);

  useEffect(() => {
    if (submitSuccess) {
      history.push(OnboardingUrls[RouteNames.Ticketing]);
    }
  }, [submitSuccess, history]);

  const filteredSubCategories = useMemo(
    (): TicketSubCategory[] =>
      categoryId ? subCategories.filter(f => f.categoryId === categoryId) : [],
    [subCategories, categoryId]
  );

  const handleDescriptionChange = useCallback(
    (value: string, _delta: unknown, _source: unknown, editor: { getText: () => string }) => {
      setDescription(value);
      setDescriptionTextLength(editor.getText().length - 1);
    },
    []
  );

  const handleCategoryChange = useCallback((value: string) => {
    setCategoryId(value);
    setSubCategoryId("");
  }, []);

  const handleAddAttachment = useCallback(() => {
    setAttachments(prev => [...prev, { url: "https://", description: "" }]);
  }, []);

  const handleRemoveAttachment = useCallback((index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleAttachmentChange = useCallback(
    (index: number, field: keyof AttachmentInput, value: string) => {
      setAttachments(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      });
    },
    []
  );

  const handleRemoveTag = useCallback((index: number) => {
    setTags(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleAddTag = useCallback(() => {
    const trimmed = newTag.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags(prev => [...prev, trimmed]);
      setNewTag("");
    }
  }, [newTag, tags]);

  const handleTagKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleAddTag();
      }
    },
    [handleAddTag]
  );

  const isValid =
    title.trim() &&
    descriptionTextLength > 0 &&
    description.trim().length <= 20000 &&
    severity &&
    categoryId &&
    subCategoryId &&
    teamId;

  const handleSubmit = useCallback(() => {
    if (!isValid) {
      return;
    }

    const request: SubmitTicketRequest = {
      title: title.trim(),
      description: description.trim(),
      type: TicketType.question,
      severity: Number(severity),
    };

    if (teamId && teamId !== "private") {
      request.teamId = teamId;
    }

    if (categoryId) {
      request.categoryId = categoryId;
    }
    if (subCategoryId) {
      request.subCategoryId = subCategoryId;
    }
    if (tags.length > 0) {
      request.tags = tags.join(", ");
    }
    if (sellerId) {
      request.accessId = sellerId;
    }

    const validAttachments = attachments.filter(
      att => att.url.trim() && att.url.trim() !== "https://"
    );
    if (validAttachments.length > 0) {
      request.attachments = validAttachments.map(att => ({
        url: att.url.trim(),
        description: att.description.trim(),
      }));
    }

    dispatch(ticketingActions.submitTicket.start(request));
  }, [
    title,
    description,
    severity,
    teamId,
    categoryId,
    subCategoryId,
    tags,
    sellerId,
    attachments,
    isValid,
    dispatch,
  ]);

  const handleBack = useCallback(() => {
    history.push(OnboardingUrls[RouteNames.Ticketing]);
  }, [history]);

  if (!isSignedIn || !isVerifyChecked) {
    return (
      <>
        <div className={pageStyles.outerContainer}>
          <div className={pageStyles.spinnerContainer}>
            <LoadingSpinner />
          </div>
        </div>
      </>
    );
  }

  if (!isVerified) {
    return (
      <AccessDeniedPage
        footNote={strings.ticketAccessDeniedMsg}
        pageType={PageType.IdXboxApplication}
      />
    );
  }

  return (
    <>
      <div className={pageStyles.outerContainer}>
        <div className={formStyles.submitPageLayout}>
          <div className={pageStyles.topBar}>
            <button className={pageStyles.backLink} onClick={handleBack}>
              ← {strings.ticketBackToList}
            </button>
          </div>

          {/* Header with title + buttons */}
          <div className={formStyles.submitPageHeader}>
            <div className={formStyles.submitPageTitleRow}>
              <h2 className={formStyles.submitPageTitle}>{strings.ticketSubmitPageTitle}</h2>
            </div>
            <div className={formStyles.submitPageActions}>
              {submitError && (
                <span className={formStyles.errorMessage} role="alert">
                  {submitError}
                </span>
              )}
              <HeroButton
                as="button"
                buttonType={ButtonType.Secondary}
                className={formStyles.cancelButton}
                onClick={handleBack}
                disabled={isSubmitting}
              >
                {strings.ticketCancelLabel}
              </HeroButton>
              <HeroButton
                as="button"
                className={formStyles.submitButton}
                onClick={handleSubmit}
                disabled={isSubmitting || !isValid}
              >
                {isSubmitting ? strings.ticketSubmittingLabel : strings.ticketSubmitLabel}
              </HeroButton>
            </div>
          </div>

          {/* Form content in white card */}
          <div className={formStyles.submitPageCard}>
            <div
              className={formStyles.modalBody}
              style={{ maxHeight: "none", overflow: "visible", padding: "20px 0" }}
            >
              {/* Title + Team row */}
              <div className={formStyles.formRow}>
                <div className={formStyles.formGroupHalf}>
                  <label htmlFor="submit-ticket-title" className={formStyles.formLabel}>
                    {strings.ticketTitleLabel}
                    <span className={formStyles.requiredMark} aria-hidden="true">
                      *
                    </span>
                  </label>
                  <input
                    id="submit-ticket-title"
                    type="text"
                    className={formStyles.formInput}
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    maxLength={200}
                    disabled={isSubmitting}
                    aria-required="true"
                  />
                  {title.length >= 160 && (
                    <span
                      className={cx(formStyles.charCount, {
                        [formStyles.charCountWarning]: title.length >= 200,
                      })}
                      aria-live="polite"
                    >
                      {title.length}/200
                    </span>
                  )}
                </div>
                <div className={formStyles.formGroupNarrow}>
                  <label htmlFor="submit-ticket-team" className={formStyles.formLabel}>
                    {strings.ticketTeamLabel}
                    <span className={formStyles.requiredMark} aria-hidden="true">
                      *
                    </span>
                  </label>
                  <SelectDropdown
                    className={formStyles.formSelect}
                    value={teamId}
                    onChange={setTeamId}
                    disabled={isSubmitting}
                    options={[
                      { id: "", label: strings.ticketSelectTeam },
                      { id: "private", label: strings.ticketTeamPrivateOption },
                      ...submitTeams.map(team => ({ id: team.id, label: team.name })),
                    ]}
                    aria-label={strings.ticketTeamLabel}
                  />
                </div>
              </div>

              {/* Description */}
              <div className={formStyles.formGroup}>
                <label id="submit-ticket-description-label" className={formStyles.formLabel}>
                  {strings.ticketDescriptionLabel}
                  <span className={formStyles.requiredMark} aria-hidden="true">
                    *
                  </span>
                </label>
                <div
                  ref={descriptionEditorRef}
                  className={formStyles.richTextEditor}
                  role="group"
                  aria-labelledby="submit-ticket-description-label"
                >
                  {ReactQuill && (
                    <ReactQuill
                      theme="snow"
                      value={description}
                      onChange={handleDescriptionChange}
                      modules={quillModules}
                      formats={quillFormats}
                      readOnly={isSubmitting}
                      placeholder={strings.ticketDescriptionLabel}
                    />
                  )}
                </div>
                {description.trim().length >= 16000 && (
                  <span
                    className={cx(formStyles.charCount, {
                      [formStyles.charCountWarning]: description.trim().length >= 20000,
                    })}
                    aria-live="polite"
                  >
                    {description.trim().length}/20000
                  </span>
                )}
              </div>

              {/* Category + Sub-category */}
              <div className={formStyles.formRow}>
                <div className={formStyles.formGroup}>
                  <label htmlFor="submit-ticket-category" className={formStyles.formLabel}>
                    {strings.ticketCategoryLabel}
                    <span className={formStyles.requiredMark} aria-hidden="true">
                      *
                    </span>
                  </label>
                  <SelectDropdown
                    className={formStyles.formSelect}
                    value={categoryId}
                    onChange={handleCategoryChange}
                    disabled={isSubmitting}
                    options={[
                      { id: "", label: strings.ticketSelectCategory },
                      ...categories.map(p => ({
                        id: p.id,
                        label: translateValue(
                          translationMap,
                          TranslationEntity.product,
                          TranslationField.name,
                          p.id,
                          p.name
                        ),
                      })),
                    ]}
                    aria-label={strings.ticketCategoryLabel}
                  />
                </div>
                <div className={formStyles.formGroup}>
                  <label htmlFor="submit-ticket-subcategory" className={formStyles.formLabel}>
                    {strings.ticketSubCategoryLabel}
                    <span className={formStyles.requiredMark} aria-hidden="true">
                      *
                    </span>
                  </label>
                  <SelectDropdown
                    className={formStyles.formSelect}
                    value={subCategoryId}
                    onChange={setSubCategoryId}
                    disabled={isSubmitting || !categoryId}
                    options={[
                      { id: "", label: strings.ticketSelectSubCategory },
                      ...filteredSubCategories.map(f => ({
                        id: f.id,
                        label: translateValue(
                          translationMap,
                          TranslationEntity.productFeature,
                          TranslationField.name,
                          f.id,
                          f.name
                        ),
                      })),
                    ]}
                    aria-label={strings.ticketSubCategoryLabel}
                  />
                </div>
              </div>

              {/* Severity + Tags */}
              <div className={formStyles.formRow}>
                <div className={formStyles.formGroup}>
                  <label htmlFor="submit-ticket-severity" className={formStyles.formLabel}>
                    {strings.ticketSeverityLabel}
                    <span className={formStyles.requiredMark} aria-hidden="true">
                      *
                    </span>
                  </label>
                  <SelectDropdown
                    className={formStyles.formSelect}
                    value={severity}
                    onChange={setSeverity}
                    disabled={isSubmitting}
                    options={[
                      { id: "", label: strings.ticketSelectSeverity },
                      ...severityOptions.map(opt => ({
                        id: String(opt.value),
                        label: translateValue(
                          translationMap,
                          TranslationEntity.ticket,
                          TranslationField.severity,
                          String(opt.value),
                          opt.label
                        ),
                      })),
                    ]}
                    aria-label={strings.ticketSeverityLabel}
                  />
                </div>
                <div className={formStyles.formGroup}>
                  <label id="submit-ticket-tags-label" className={formStyles.formLabel}>
                    {strings.ticketTagsLabel}
                  </label>
                  <div
                    className={formStyles.tagsList}
                    role="group"
                    aria-labelledby="submit-ticket-tags-label"
                  >
                    {tags.map((t, i) => (
                      <span key={i} className={formStyles.tag}>
                        {t}
                        <button
                          className={formStyles.tagRemove}
                          onClick={() => handleRemoveTag(i)}
                          aria-label={strings.ticketRemoveTagAriaLabel.replace("{0}", t)}
                          disabled={isSubmitting}
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      className={formStyles.addTagInput}
                      value={newTag}
                      onChange={e => setNewTag(e.target.value)}
                      onKeyDown={handleTagKeyDown}
                      placeholder={strings.ticketTagsPlaceholder}
                      maxLength={50}
                      disabled={isSubmitting}
                      aria-label={strings.ticketTagInputAriaLabel}
                    />
                  </div>
                </div>
              </div>

              {/* Attachments */}
              <div className={formStyles.formGroup}>
                <label id="submit-ticket-attachments-label" className={formStyles.formLabel}>
                  {strings.ticketRelatedLinksLabel}
                </label>
                {attachments.map((att, index) => (
                  <div key={index} className={formStyles.attachmentRow}>
                    <div className={formStyles.attachmentField}>
                      <input
                        type="text"
                        className={formStyles.formInput}
                        value={att.url}
                        onChange={e => handleAttachmentChange(index, "url", e.target.value)}
                        placeholder={strings.ticketLinkUrlPlaceholder}
                        maxLength={1000}
                        disabled={isSubmitting}
                        aria-label={strings.ticketAttachmentUrlAriaLabel.replace(
                          "{0}",
                          String(index + 1)
                        )}
                      />
                      {att.url.length >= 800 && (
                        <span
                          className={cx(formStyles.charCount, {
                            [formStyles.charCountWarning]: att.url.length >= 1000,
                          })}
                          aria-live="polite"
                        >
                          {att.url.length}/1000
                        </span>
                      )}
                    </div>
                    <div className={formStyles.attachmentField}>
                      <input
                        type="text"
                        className={formStyles.formInput}
                        value={att.description}
                        onChange={e => handleAttachmentChange(index, "description", e.target.value)}
                        placeholder={strings.ticketLinkDescPlaceholder}
                        maxLength={800}
                        disabled={isSubmitting}
                        aria-label={strings.ticketAttachmentDescAriaLabel.replace(
                          "{0}",
                          String(index + 1)
                        )}
                      />
                      {att.description.length >= 640 && (
                        <span
                          className={cx(formStyles.charCount, {
                            [formStyles.charCountWarning]: att.description.length >= 800,
                          })}
                          aria-live="polite"
                        >
                          {att.description.length}/800
                        </span>
                      )}
                    </div>
                    {attachments.length > 1 && (
                      <button
                        className={formStyles.removeButton}
                        onClick={() => handleRemoveAttachment(index)}
                        aria-label={strings.ticketRemoveAttachmentAriaLabel}
                        disabled={isSubmitting}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                <button
                  className={formStyles.addAttachmentButton}
                  onClick={handleAddAttachment}
                  disabled={isSubmitting}
                  aria-label={strings.ticketAddLinkAriaLabel}
                >
                  {strings.ticketAddLinkLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TicketingSubmitPage;
